import currency from "currency.js";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  GetAccount200ResponsePositionsInner,
  GetTradeHistory200ResponseTradeHistoryInner,
  SideResponse,
} from "../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { getTradeEntryPrice } from "../../utils/trade";
import { formatKnownPrice } from "../../utils/unknownNumbers";
import { getDisplayInstrumentName } from "../../utils/displayName";
import { getAppDomain } from "config/chains";
import {
  CardBranding,
  CardChartBg,
  CardContent,
  CardPnlText,
  CardPriceItem,
  CardPriceRow,
  CardTopRow,
  DirectionBadge,
  PnLShareCard,
} from "./style";
import { CARD_DIMS, getExportSafeAssetLogo } from "./helpers";
import { IPnLCardInnerProps } from "./types";

/** Simple decorative sparkline SVG overlay */
function SparklineSvg({
  isPositive,
  width,
  height,
}: {
  isPositive: boolean;
  width: number;
  height: number;
}) {
  const color = isPositive ? COLORS.up.one : COLORS.down.one;
  const fillId = `spark-fill-${isPositive ? "pos" : "neg"}`;

  // Build a smooth wavy path trending up (positive) or down (negative)
  const pts = isPositive
    ? [
        [0, height * 0.82],
        [width * 0.12, height * 0.75],
        [width * 0.25, height * 0.7],
        [width * 0.38, height * 0.6],
        [width * 0.5, height * 0.55],
        [width * 0.62, height * 0.42],
        [width * 0.75, height * 0.32],
        [width * 0.88, height * 0.18],
        [width, height * 0.08],
      ]
    : [
        [0, height * 0.15],
        [width * 0.12, height * 0.22],
        [width * 0.25, height * 0.28],
        [width * 0.38, height * 0.38],
        [width * 0.5, height * 0.45],
        [width * 0.62, height * 0.56],
        [width * 0.75, height * 0.65],
        [width * 0.88, height * 0.76],
        [width, height * 0.88],
      ];

  // Smooth bezier from point array
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const cpx = (prev[0] + curr[0]) / 2;
    d += ` C ${cpx.toFixed(1)} ${prev[1].toFixed(1)}, ${cpx.toFixed(1)} ${curr[1].toFixed(1)}, ${curr[0].toFixed(1)} ${curr[1].toFixed(1)}`;
  }

  const fillPath = `${d} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${fillId})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="2" opacity="0.85" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// PnLCard – shared between preview and hidden export target
// ---------------------------------------------------------------------------

export function PnLCardInner({
  pnlType,
  pnlData,
  pnlContent,
  theme,
}: IPnLCardInnerProps) {
  const { getMarketPrecision } = useContext(MarketInstrumentContext);
  const { t } = useTranslation("app", { keyPrefix: "PnLCardModal" });

  const assetLogo = getExportSafeAssetLogo(pnlData?.asset);
  const pricePrecision = getMarketPrecision(
    pnlData?.asset,
    pnlData?.instrument_type
  ).price_precision;

  const isPositive = Number(pnlContent?.text?.replace(/[^0-9.-]/g, "")) >= 0;
  const appDomain = getAppDomain().toUpperCase();

  // Direction badge
  const isLong =
    pnlType === "position"
      ? pnlData?.side === SideResponse.Buy
      : pnlData?.side === SideResponse.Sell;

  const leverage = (
    pnlData as GetAccount200ResponsePositionsInner | undefined
  )?.leverage;

  const directionLabel = `${isLong ? "Long" : "Short"}${leverage ? ` ${Math.round(Number(leverage))}x` : ""}`;

  // Entry / Mark (or Exit) prices
  const entryPrice =
    pnlType === "position"
      ? currency(pnlData?.avg_entry_price || 0, {
          precision: pricePrecision,
        }).format()
      : currency(getTradeEntryPrice(
          pnlData as GetTradeHistory200ResponseTradeHistoryInner | undefined
        ) || 0, { precision: pricePrecision }).format();

  const secondPrice =
    pnlType === "position"
      ? formatKnownPrice(
          (pnlData as GetAccount200ResponsePositionsInner | undefined)
            ?.mark_price,
          pricePrecision
        )
      : currency(
          (pnlData as GetTradeHistory200ResponseTradeHistoryInner | undefined)
            ?.price || 0,
          { precision: pricePrecision }
        ).format();

  const secondLabel =
    pnlType === "position" ? t("current_price", { defaultValue: "Mark Price" }) : t("exit_price");

  const dims = CARD_DIMS[theme.orientation];

  return (
    <PnLShareCard {...theme} isPositive={isPositive}>
      {theme.bgMode === "chart" && (
        <CardChartBg>
          <SparklineSvg
            isPositive={isPositive}
            width={dims.width}
            height={dims.height}
          />
        </CardChartBg>
      )}
      <CardContent>
        {/* Top row: icon + name + badge */}
        <CardTopRow>
          <img src={assetLogo} alt={pnlData?.asset} crossOrigin="anonymous" />
          <span>{getDisplayInstrumentName(pnlData?.instrument_name) ?? "—"}</span>
          <DirectionBadge isLong={!!isLong}>{directionLabel}</DirectionBadge>
        </CardTopRow>

        {/* Price row */}
        <CardPriceRow>
          <CardPriceItem>
            <span>{t("entry_price")}</span>
            <span>{entryPrice}</span>
          </CardPriceItem>
          <CardPriceItem>
            <span>{secondLabel}</span>
            <span>{secondPrice}</span>
          </CardPriceItem>
        </CardPriceRow>

        {/* PnL */}
        <CardPnlText
          color={pnlContent?.color ?? TEXT_COLORS.one}
          orientation={theme.orientation}
          key={pnlContent?.text}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {pnlContent?.text ?? "—"}
        </CardPnlText>

        {/* Branding */}
        <CardBranding>
          <span>APP.{appDomain}</span>
        </CardBranding>
      </CardContent>
    </PnLShareCard>
  );
}
